// The app language for code that runs OUTSIDE React components — date helpers,
// formatters, labels built from stored data. LanguageProvider keeps it in sync
// (setAppLanguage); components re-render on a language switch, so helpers called
// during render always see the current language.
//
// Why this exists: helpers like fmt() / dateLabel() were hard-coded to 'en-GB',
// and values stored in English in the database ("Vacant", "Trade License",
// "1st Installment") were shown verbatim — a German user saw English dates and
// labels on every unit page, report and valuation (audit 2026-10-22).

export type AppLanguage = 'en' | 'de';

let current: AppLanguage = (() => {
  try { return localStorage.getItem('investmenterLanguage') === 'de' ? 'de' : 'en'; } catch { return 'en'; }
})();

export const setAppLanguage = (lang: AppLanguage) => { current = lang === 'de' ? 'de' : 'en'; };
export const appLanguage = (): AppLanguage => current;
/** Locale for toLocaleDateString / Intl: German users get German month names. */
export const appLocale = (): string => (current === 'de' ? 'de-DE' : 'en-GB');

export const monthNames = (): string[] => current === 'de'
  ? ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
  : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const monthAbbr = (): string[] => current === 'de'
  ? ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
  : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Values the database stores in English, shown to the owner as labels.
// Keys are lower-case; unknown values are returned unchanged (never blanked).
const DATA_DE: Record<string, string> = {
  // occupancy / unit status
  vacant: 'Leerstehend', occupied: 'Vermietet', rented: 'Vermietet', 'off-plan': 'Im Bau', offplan: 'Im Bau',
  'off plan': 'Im Bau', ready: 'Fertiggestellt', active: 'Aktiv', sold: 'Verkauft', listed: 'Inseriert',
  'under construction': 'Im Bau', completed: 'Fertiggestellt', 'owner occupied': 'Eigennutzung', 'owner-occupied': 'Eigennutzung',
  // payment / appointment status
  pending: 'Ausstehend', paid: 'Bezahlt', partial: 'Teilweise bezahlt', overdue: 'Überfällig', due: 'Fällig',
  scheduled: 'Geplant', confirmed: 'Bestätigt', cancelled: 'Storniert', canceled: 'Storniert', open: 'Offen', closed: 'Erledigt',
  'in progress': 'In Bearbeitung', resolved: 'Erledigt', verified: 'Geprüft', rejected: 'Abgelehnt',
  // property types
  apartment: 'Wohnung', villa: 'Villa', townhouse: 'Reihenhaus', penthouse: 'Penthouse', studio: 'Studio',
  office: 'Büro', retail: 'Gewerbefläche', land: 'Grundstück', duplex: 'Maisonette',
  // expense types / maintenance categories
  maintenance: 'Instandhaltung', 'service charge': 'Servicegebühr', 'service charges': 'Servicegebühren',
  'management fee': 'Verwaltungsgebühr', utilities: 'Nebenkosten', other: 'Sonstiges', repairs: 'Reparaturen',
  plumbing: 'Sanitär', electrical: 'Elektrik', 'ac / hvac': 'Klimaanlage', ac: 'Klimaanlage', cleaning: 'Reinigung',
  // document types
  document: 'Dokument', unknown: 'Unbekannt', 'trade license': 'Handelslizenz', passport: 'Reisepass', visa: 'Visum',
  'tenancy contract': 'Mietvertrag', 'payment proof': 'Zahlungsnachweis', 'floor plan': 'Grundriss', brochure: 'Broschüre',
  'booking form': 'Buchungsformular', 'reservation agreement': 'Reservierungsvereinbarung', 'power of attorney': 'Vollmacht',
  'snagging report': 'Mängelbericht', 'soa document': 'Kontoauszug (SOA)', receipt: 'Quittung', 'title deed': 'Eigentumsurkunde',
  'memorandum of association': 'Gesellschaftsvertrag', 'other documents': 'Sonstige Dokumente',
  'corporate services': 'Unternehmensservices', 'conveyance services': 'Übertragungsservices',
};

/** A stored English label in the current language ("Vacant" → "Leerstehend"). */
export function trData(value?: string | null): string {
  if (value === undefined || value === null) return '';
  const s = String(value);
  if (current !== 'de') return s;
  return DATA_DE[s.trim().toLowerCase()] || s;
}

/**
 * Instalment names as the AI read them off SPAs / SOAs ("Down Payment",
 * "3rd Installment", "On Completion"), in the current language. Anything it
 * does not recognise is shown as stored.
 */
export function trMilestone(desc?: string | null): string {
  const s = String(desc || '').trim();
  if (current !== 'de' || !s) return s;
  const rules: Array<[RegExp, string | ((...m: string[]) => string)]> = [
    [/^(\d+)\s*(st|nd|rd|th)\s*instal(l)?ment$/i, (_m, n) => `${n}. Rate`],
    [/^instal(l)?ment\s*(no\.?\s*)?(\d+)$/i, (_m, _l, _n, n) => `Rate ${n}`],
    [/^(first|1st)\s*instal(l)?ment$/i, '1. Rate'],
    [/^down\s*payment$/i, 'Anzahlung'],
    [/^(booking|reservation)(\s*(fee|amount|deposit|payment))?$/i, 'Reservierung'],
    [/^(on\s*)?(handover|completion)(\s*payment)?$/i, 'Bei Übergabe'],
    [/^dld\s*(fee|registration(\s*fee)?)$/i, 'DLD-Gebühr'],
    [/^(oqood|registration)\s*fee$/i, 'Registrierungsgebühr'],
    [/^admin(istration)?\s*fee$/i, 'Verwaltungsgebühr'],
  ];
  for (const [re, out] of rules) {
    const m = s.match(re);
    if (m) return typeof out === 'string' ? out : out(...(m as unknown as string[]));
  }
  // "20% on 40% construction completion" style
  return s
    .replace(/\binstal(l)?ment\b/gi, 'Rate')
    .replace(/\bconstruction completion\b/gi, 'Baufortschritt')
    .replace(/\bon completion\b/gi, 'bei Fertigstellung')
    .replace(/\bon handover\b/gi, 'bei Übergabe')
    .replace(/\bdown payment\b/gi, 'Anzahlung')
    .replace(/%\s+on\s+/gi, '% bei ');
}

/**
 * An error for the user: the server message in English, the translated
 * fallback in German. Server messages are English-only, and showing them first
 * put "Invalid credentials" on a German login screen. When the server message
 * carries specifics (a number: minutes to wait, attempts left) a German user
 * still gets them, in brackets.
 */
export function uiError(serverMsg: string | undefined | null, fallback: string): string {
  if (current !== 'de') return serverMsg || fallback;
  if (serverMsg && /\d/.test(serverMsg)) return `${fallback} (${serverMsg})`;
  return fallback;
}
